import React from 'react';
import { TranslationContent } from '../types';

interface HeroProps {
  t: TranslationContent['hero'];
  onContactClick: () => void;
}

const LaptopIllustration: React.FC = () => {
  return (
    <div className="relative w-full max-w-2xl mx-auto flex items-center justify-center py-4">
      {/* Brilho de fundo suave */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[300px] h-[200px] bg-indigo-100/30 rounded-full blur-[80px]" />
      </div>

      {/* Laptop Wrapper */}
      <div className="relative w-full aspect-[16/10] animate-float z-10">
        
        {/* Screen/Lid */}
        <div className="relative w-[90%] mx-auto h-[85%] bg-slate-900 rounded-t-2xl border-[6px] border-slate-800 shadow-2xl overflow-hidden flex flex-col">
          {/* Webcam */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-slate-700 rounded-full z-20" />
          
          {/* Reflexo de Vidro */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none z-30" />

          {/* Browser Interface / Website Content */}
          <div className="flex-grow bg-white m-0.5 rounded-t-lg overflow-hidden flex flex-col">
            {/* Browser Header */}
            <div className="h-6 bg-slate-100 border-b border-slate-200 flex items-center px-3 gap-1.5">
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
              </div>
              <div className="h-3 w-1/2 bg-white rounded-md mx-auto border border-slate-200" />
            </div>

            {/* Website Layout Content */}
            <div className="flex-grow overflow-hidden flex flex-col">
              {/* Nav Bar */}
              <div className="h-10 border-b border-slate-50 flex items-center justify-between px-6">
                <div className="w-16 h-2 bg-indigo-100 rounded-full" />
                <div className="flex gap-3">
                  <div className="w-8 h-1.5 bg-slate-100 rounded-full" />
                  <div className="w-8 h-1.5 bg-slate-100 rounded-full" />
                  <div className="w-8 h-1.5 bg-slate-100 rounded-full" />
                </div>
              </div>

              {/* Website Hero Section */}
              <div className="p-6 grid grid-cols-2 gap-4 items-center bg-slate-50/50">
                <div className="space-y-3">
                  <div className="w-full h-3 bg-slate-200 rounded-full" />
                  <div className="w-3/4 h-3 bg-slate-200 rounded-full" />
                  <div className="w-1/2 h-2 bg-indigo-200 rounded-full" />
                  <div className="pt-2">
                    <div className="w-20 h-6 bg-indigo-600 rounded-lg shadow-sm" />
                  </div>
                </div>
                <div className="h-24 bg-white rounded-xl border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden">
                   <div className="w-full h-full bg-gradient-to-tr from-indigo-50 to-white flex items-center justify-center">
                     <svg className="w-10 h-10 text-indigo-100" fill="currentColor" viewBox="0 0 24 24">
                       <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                     </svg>
                   </div>
                </div>
              </div>

              {/* Website Cards/Body */}
              <div className="p-6 grid grid-cols-3 gap-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="space-y-2">
                    <div className="w-full h-12 bg-white rounded-lg border border-slate-100" />
                    <div className="w-3/4 h-1.5 bg-slate-100 rounded-full" />
                    <div className="w-full h-1 bg-slate-50 rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Laptop Body/Keyboard Base */}
        <div className="relative w-full h-[15%] bg-slate-800 rounded-b-xl shadow-xl flex items-center justify-center group">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-1.5 bg-slate-900 rounded-b-md" />
          <div className="w-24 h-6 border border-slate-700/50 rounded-md mt-1" />
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[90%] h-4 bg-slate-900/10 blur-xl rounded-full" />
        </div>
      </div>
    </div>
  );
}

const Hero: React.FC<HeroProps> = ({ t, onContactClick }) => {
  return (
    <section id="hero" className="relative overflow-hidden pt-12 pb-20 md:pt-24 md:pb-28 lg:pt-32 lg:pb-36 bg-white scroll-mt-20">
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[600px] h-[600px] bg-indigo-50/40 rounded-full blur-3xl opacity-60" />
      
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="max-w-2xl text-center lg:text-left z-10 order-2 lg:order-1">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold tracking-wide mb-6">
              {t.tag}
            </div>
            <h1 className="text-3xl md:text-5xl xl:text-6xl font-extrabold tracking-tight text-slate-900 mb-6 md:mb-8 leading-[1.1]">
              {t.headline}
            </h1>
            <p className="text-base md:text-xl text-slate-600 mb-8 md:mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0">
              {t.subheadline}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  onContactClick();
                }}
                className="inline-flex items-center justify-center px-10 py-4 text-base font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200 active:scale-95 transform hover:-translate-y-0.5"
              >
                {t.ctaContact}
              </button>
            </div>

            <div className="mt-10 flex items-center justify-center lg:justify-start space-x-6 md:space-x-8">
              <span className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap text-slate-600">{t.globalDelivery}</span>
              <div className="w-px h-4 bg-slate-300" />
              <span className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap text-slate-600">{t.directContact}</span>
            </div>
          </div>

          <div className="order-1 lg:order-2 relative flex justify-center items-center w-full">
             <LaptopIllustration />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;